import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  Typography,
  Tag,
  Divider,
  Tabs,
  Table,
  Button,
  message,
  Popconfirm,
  Input,
  Space
} from 'antd';
import { 
  RobotOutlined, 
  ArrowUpOutlined, 
  ArrowDownOutlined,
  HistoryOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EditOutlined,
  UndoOutlined
} from '@ant-design/icons';
import aiAnalysisService from '../services/aiAnalysisService';
import moment from 'moment';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

// Stocks 페이지 기본 Prompt (사용자가 수정 가능)
const DEFAULT_STOCKS_PROMPT = `당신은 전문 금융 애널리스트입니다. {analysis_date} 현재, {stock_name}({symbol}) 주가가 {change_info}했습니다.

주가 흐름을 다음 요건에 맞춰 분석해 주세요.
"반드시 구글 검색을 통해 오늘 기준 가장 최근 거래일 종가와 최신 시장 뉴스를 먼저 확인한 뒤, 그 데이터를 바탕으로 분석해줘. 학습된 과거 데이터로 추측하지 마."
시장 요약: 종가, 등락률, 거래량 등 핵심 지표를 표나 리스트로 정리할 것.
시장 맥락(Context): 당일 전체 시장(코스피/코스닥) 지수 흐름과 비교하여 상대적인 강세를 분석할 것.
상승/하락 요인: 반도체 업황(HBM, 온디바이스 AI 등)이나 기업 고유 이슈를 연계하여 설명할 것.
기술적/재무적 관점: 주요 보조지표(RSI, MACD 등)나 최근 실적 기반의 밸류에이션 의견을 포함할 것.
가독성: 핵심 내용은 굵게 표시하고, 섹션을 나누어 가독성 있게 출력할 것.
전문적이고 구체적으로 분석해주세요. 정확한 실제 데이터와 날짜 및 출처를 포함해주세요.`;

const StockAnalysisModal = ({
  visible,
  selectedStock,
  onClose,
  analysisType = 'company'  // 기본값: 'company' (Stocks 페이지용), Account Balance는 'market' 전달
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  
  // 히스토리 관련 state
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);
  
  // Prompt 관련 state
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_STOCKS_PROMPT);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [isPromptModified, setIsPromptModified] = useState(false);  // prompt 수정 여부 추적

  // 모달이 열릴 때 초기화
  useEffect(() => {
    if (visible && selectedStock) {
      setActiveTab('1');
      setAnalysisResult(null);
      setError(null);
      setSelectedHistory(null);
      setIsPromptModified(false);  // prompt 수정 상태 초기화
    } else {
      // 모달이 닫힐 때 상태 초기화
      setAnalysisResult(null);
      setError(null);
      setLoading(false);
      setHistoryData([]);
      setSelectedHistory(null);
      setIsPromptModified(false);
    }
  }, [visible, selectedStock]);

  // AI 분석 실행
  const handleAIAnalysis = async () => {
    if (!selectedStock) return;

    setLoading(true);
    setAnalysisResult(null);
    setError(null);

    try {
      const requestData = {
        symbol: selectedStock.code,
        stock_name: selectedStock.name,
        current_price: selectedStock.current_price || 0,
        prev_day_change: selectedStock.prev_day_change || 0,
        prev_day_change_percent: selectedStock.prev_day_change_percent || 0,
        date: new Date().toISOString().split('T')[0],
        analysis_type: analysisType  // 분석 타입 전달 (company 또는 market)
      };

      // 사용자가 prompt를 수정한 경우에만 custom_prompt 전달
      if (isPromptModified) {
        requestData.custom_prompt = customPrompt;
      }

      const response = await aiAnalysisService.analyzeStockMovement(requestData);

      if (response.success) {
        setAnalysisResult(response.data);
        message.success('AI 분석이 완료되었습니다');
        
        // 분석 완료 후 히스토리 탭이 활성화되어 있으면 새로고침
        if (activeTab === '2') {
          loadAnalysisHistory();
        }
      } else {
        setError(response.error || 'AI 분석 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error('AI 분석 오류:', err);
      setError(
        err.response?.data?.error || 
        err.message || 
        'AI 분석 중 오류가 발생했습니다. GEMINI_API_KEY가 설정되어 있는지 확인하세요.'
      );
    } finally {
      setLoading(false);
    }
  };

  // 히스토리 목록 로드
  const loadAnalysisHistory = async () => {
    if (!selectedStock) {
      message.warning('종목 정보를 찾을 수 없습니다');
      return;
    }

    setHistoryLoading(true);
    try {
      // stock_id가 있으면 id로, 없으면 symbol로 조회
      const options = selectedStock.id 
        ? { stockId: selectedStock.id, limit: 10 }
        : { symbol: selectedStock.code, limit: 10 };
      
      const response = await aiAnalysisService.getAnalysisHistory(options);
      if (response.success) {
        setHistoryData(response.results || []);
      } else {
        message.error('히스토리 조회에 실패했습니다');
      }
    } catch (err) {
      console.error('히스토리 조회 오류:', err);
      message.error('히스토리 조회 중 오류가 발생했습니다');
    } finally {
      setHistoryLoading(false);
    }
  };

  // 히스토리 상세 보기
  const handleViewHistory = async (record) => {
    try {
      const response = await aiAnalysisService.getAnalysisDetail(record.id);
      if (response.success) {
        setSelectedHistory(response.data);
      } else {
        message.error('상세 정보 조회에 실패했습니다');
      }
    } catch (err) {
      console.error('상세 조회 오류:', err);
      message.error('상세 정보 조회 중 오류가 발생했습니다');
    }
  };

  // 히스토리 삭제
  const handleDeleteHistory = async (analysisId) => {
    try {
      const response = await aiAnalysisService.deleteAnalysisHistory(analysisId);
      if (response.success) {
        message.success('분석 히스토리가 삭제되었습니다');
        loadAnalysisHistory(); // 목록 새로고침
        setSelectedHistory(null); // 상세 보기 닫기
      } else {
        message.error('삭제에 실패했습니다');
      }
    } catch (err) {
      console.error('삭제 오류:', err);
      message.error('삭제 중 오류가 발생했습니다');
    }
  };

  // 탭 변경 시 히스토리 로드
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === '2' && selectedStock) {
      loadAnalysisHistory();
    }
  };

  const handleModalClose = () => {
    setAnalysisResult(null);
    setError(null);
    setLoading(false);
    setHistoryData([]);
    setSelectedHistory(null);
    setActiveTab('1');
    setIsPromptModified(false);
    onClose();
  };

  if (!selectedStock) {
    return null;
  }

  // 히스토리 테이블 컬럼
  const historyColumns = [
    {
      title: '분석일',
      dataIndex: 'analysis_date',
      key: 'analysis_date',
      render: (date) => moment(date).format('YYYY-MM-DD'),
      width: 120,
    },
    {
      title: '타입',
      dataIndex: 'analysis_type_display',
      key: 'analysis_type',
      render: (type) => {
        const color = type === '시황 분석' ? 'green' : 'blue';
        return <Tag color={color}>{type}</Tag>;
      },
      width: 100,
    },
    {
      title: '가격',
      dataIndex: 'current_price',
      key: 'current_price',
      render: (price) => `₩${Number(price).toLocaleString()}`,
      width: 120,
    },
    {
      title: '변동률',
      dataIndex: 'prev_day_change_percent',
      key: 'prev_day_change_percent',
      render: (percent) => {
        if (percent === null || percent === undefined) return '-';
        const numPercent = Number(percent);
        if (isNaN(numPercent)) return '-';
        const color = numPercent >= 0 ? 'green' : 'red';
        const sign = numPercent >= 0 ? '+' : '';
        return <Text style={{ color }}>{sign}{numPercent.toFixed(2)}%</Text>;
      },
      width: 100,
    },
    {
      title: '분석 요약',
      dataIndex: 'analysis_summary',
      key: 'analysis_summary',
      ellipsis: true,
    },
    {
      title: '생성시간',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => moment(time).format('MM-DD HH:mm'),
      width: 120,
    },
    {
      title: '작업',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <div>
          <Button 
            type="link" 
            size="small"
            onClick={() => handleViewHistory(record)}
          >
            상세
          </Button>
          <Popconfirm
            title="정말 삭제하시겠습니까?"
            onConfirm={() => handleDeleteHistory(record.id)}
            okText="삭제"
            cancelText="취소"
          >
            <Button 
              type="link" 
              danger 
              size="small"
              icon={<DeleteOutlined />}
            >
              삭제
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <RobotOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          <span>AI 주가 분석</span>
          <Tag color="blue" style={{ marginLeft: '8px' }}>
            {selectedStock.name} ({selectedStock.code})
          </Tag>
        </div>
      }
      open={visible}
      onCancel={handleModalClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        {/* 탭 1: 새 분석 */}
        <TabPane tab="새 분석" key="1">
          {/* Prompt 편집기 */}
          <Card 
            size="small" 
            style={{ marginBottom: '16px', backgroundColor: '#f0f5ff' }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span><EditOutlined /> 분석 Prompt 설정</span>
                <Space>
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={() => {
                      setCustomPrompt(DEFAULT_STOCKS_PROMPT);
                      setIsPromptModified(false);
                    }}
                  >
                    기본값 복원
                  </Button>
                  <Button
                    size="small"
                    type={showPromptEditor ? 'default' : 'primary'}
                    onClick={() => setShowPromptEditor(!showPromptEditor)}
                  >
                    {showPromptEditor ? '접기' : '편집'}
                  </Button>
                </Space>
              </div>
            }
          >
            {showPromptEditor ? (
              <>
                <Alert
                  message="사용 가능한 변수"
                  description={
                    <div>
                      <Text code>{'{analysis_date}'}</Text> - 분석 날짜 |{' '}
                      <Text code>{'{stock_name}'}</Text> - 종목명 |{' '}
                      <Text code>{'{symbol}'}</Text> - 종목 심볼 |{' '}
                      <Text code>{'{change_info}'}</Text> - 변동 정보
                    </div>
                  }
                  type="info"
                  style={{ marginBottom: '12px' }}
                  showIcon
                />
                <TextArea
                  value={customPrompt}
                  onChange={(e) => {
                    setCustomPrompt(e.target.value);
                    setIsPromptModified(true);
                  }}
                  rows={12}
                  placeholder="AI 분석 prompt를 입력하세요..."
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </>
            ) : (
              <div style={{ padding: '8px 0' }}>
                <Text type="secondary">
                  {isPromptModified 
                    ? "사용자 정의 prompt가 설정되었습니다. 기본값으로 복원하려면 '기본값 복원' 버튼을 누르세요."
                    : "기본 prompt가 설정되어 있습니다. analysis_type에 따라 백엔드에서 적절한 prompt를 사용합니다. 수정하려면 '편집' 버튼을 누르세요."
                  }
                </Text>
              </div>
            )}
          </Card>

          {!analysisResult && !loading && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Button 
                type="primary" 
                size="large"
                icon={<RobotOutlined />}
                onClick={handleAIAnalysis}
              >
                AI 분석 실행
              </Button>
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary">
                  Gemini AI가 {selectedStock.name}의 주가 변동을 분석합니다
                </Text>
              </div>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>AI가 주가 변동을 분석하고 있습니다...</Text>
              </div>
            </div>
          )}

          {error && (
            <Alert
              message="AI 분석 실패"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          {analysisResult && (
            <div>
              {/* 종목 정보 */}
              <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#f6ffed' }}>
                <Row gutter={16}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="현재가"
                      value={analysisResult.current_price || 0}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '16px' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="전일대비"
                      value={analysisResult.prev_day_change_percent || 0}
                      precision={2}
                      suffix="%"
                      valueStyle={{ 
                        fontSize: '16px',
                        color: (analysisResult.prev_day_change_percent || 0) >= 0 ? '#52c41a' : '#ff4d4f'
                      }}
                      prefix={
                        (analysisResult.prev_day_change_percent || 0) >= 0 ? 
                        <ArrowUpOutlined /> : <ArrowDownOutlined />
                      }
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <div style={{ fontSize: '12px', color: '#666' }}>분석 날짜</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                      {analysisResult.analysis_date}
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div style={{ fontSize: '12px', color: '#666' }}>생성 시간</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                      {moment(analysisResult.generated_at).format('HH:mm:ss')}
                    </div>
                  </Col>
                </Row>
              </Card>

              {/* AI 분석 결과 */}
              <Card size="small">
                <Paragraph style={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.8',
                  fontSize: '14px'
                }}>
                  {analysisResult.analysis}
                </Paragraph>
              </Card>
            </div>
          )}
        </TabPane>

        {/* 탭 2: 분석 히스토리 */}
        <TabPane 
          tab={
            <span>
              <HistoryOutlined />
              분석 히스토리
            </span>
          } 
          key="2"
        >
          <div style={{ marginBottom: '16px' }}>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadAnalysisHistory}
              loading={historyLoading}
            >
              새로고침
            </Button>
          </div>

          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>히스토리를 불러오는 중...</Text>
              </div>
            </div>
          ) : selectedHistory ? (
            // 상세 보기
            <div>
              <Button 
                onClick={() => setSelectedHistory(null)}
                style={{ marginBottom: '16px' }}
              >
                ← 목록으로
              </Button>
              
              <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#f6ffed' }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="분석일"
                      value={moment(selectedHistory.analysis_date).format('YYYY-MM-DD')}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="현재가"
                      value={selectedHistory.current_price}
                      precision={0}
                      prefix="₩"
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="변동률"
                      value={selectedHistory.prev_day_change_percent || 0}
                      precision={2}
                      suffix="%"
                      valueStyle={{ 
                        fontSize: '14px',
                        color: (selectedHistory.prev_day_change_percent || 0) >= 0 ? '#52c41a' : '#ff4d4f'
                      }}
                    />
                  </Col>
                  <Col span={6}>
                    <div style={{ fontSize: '12px', color: '#666' }}>AI 모델</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '4px' }}>
                      <Tag color="purple">{selectedHistory.model_used}</Tag>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card size="small">
                <Paragraph style={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.8',
                  fontSize: '14px'
                }}>
                  {selectedHistory.analysis_result}
                </Paragraph>
                <Divider />
                <div style={{ textAlign: 'right', fontSize: '12px', color: '#999' }}>
                  생성 시간: {moment(selectedHistory.created_at).format('YYYY-MM-DD HH:mm:ss')}
                </div>
              </Card>
            </div>
          ) : (
            // 목록
            <Table
              dataSource={historyData}
              columns={historyColumns}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              size="small"
              locale={{
                emptyText: '분석 히스토리가 없습니다'
              }}
            />
          )}
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default StockAnalysisModal;
